import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { b2bGetContactOptions, b2bGetStreams } from '../../lib/adminApi';

const ORANGE = '#F37428';
const NAVY   = '#0a2540';
const SIZE_OPTIONS = ['1–50', '51–200', '201–500', '501–2,000', '2,001–10,000', '10,000+'];

function dashFetch(path, opts) {
  return fetch(
    (import.meta.env.VITE_API_URL || 'https://masterfileai-api.azurewebsites.net') + path +
    (path.includes('?') ? '&' : '?') + 'token=' + (localStorage.getItem('mfai_admin_token') || ''),
    { method: opts?.method || 'GET', headers: { 'Content-Type': 'text/plain' },
      body: opts?.body !== undefined ? JSON.stringify(opts.body) : undefined }
  ).then(r => r.json());
}

function tryParse(s) { try { return s ? JSON.parse(s) : {}; } catch { return {}; } }

function timeAgo(d) {
  if (!d) return '—';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Toggle({ on, onChange, disabled }) {
  return (
    <div onClick={disabled ? undefined : () => onChange(!on)} style={{
      width: 36, height: 20, borderRadius: 10, background: on ? ORANGE : '#e2e8f0',
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
      transition: 'background 0.2s', opacity: disabled ? 0.6 : 1,
    }}>
      <div style={{ position: 'absolute', width: 16, height: 16, background: '#fff',
        borderRadius: '50%', top: 2, left: on ? 18 : 2, transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

function Chip({ label, on, onClick }) {
  return (
    <span onClick={onClick} style={{
      padding: '4px 11px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
      border: `1px solid ${on ? ORANGE : '#e2e8f0'}`,
      background: on ? 'rgba(243,116,40,0.08)' : '#f8fafc',
      color: on ? '#c2410c' : '#64748b',
      fontWeight: on ? 600 : 400, userSelect: 'none', transition: 'all 0.1s',
    }}>{label}</span>
  );
}

function RateBar({ rate, color }) {
  const pct = parseFloat(rate) || 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: '#374151' }}>{pct}%</span>
    </div>
  );
}

function HelpIcon({ tip }) {
  return (
    <span title={tip} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: '50%', background: '#e2e8f0',
      color: '#64748b', fontSize: 9, fontWeight: 700, cursor: 'help', flexShrink: 0, verticalAlign: 'middle',
    }}>?</span>
  );
}

function CardHead({ title, sub, right }) {
  return (
    <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function CardFooter({ label, sub, toggleVal, onToggle, onSave, saving }) {
  return (
    <div style={{ padding: '10px 18px', borderTop: '1px solid #f1f5f9', display: 'flex',
      alignItems: 'center', justifyContent: 'space-between', background: '#fafcff' }}>
      <div>
        <div style={{ fontSize: 12, color: '#374151' }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Toggle on={toggleVal} onChange={onToggle} />
        <button onClick={onSave} disabled={saving} style={s.btnSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function Dash() {
  const [loading, setLoading]         = useState(true);
  const [summary, setSummary]         = useState(null);
  const [contactOpts, setContactOpts] = useState({ standard_titles: [], industries: [], size_buckets: [] });
  const [streams, setStreams]         = useState([]);
  const [inbox, setInbox]             = useState([]);
  const [activeTab, setActiveTab]     = useState('outreach');
  const [resultsRange, setResultsRange] = useState('7d');
  const [results, setResults]         = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  // Targeting
  const [targeting, setTargeting]     = useState({
    contacts_per_day: 10, daily_send_cap: 20,
    industries: [], sizes: [], titles: [], auto_send: false, exclude_in_list: false,
  });
  const [activeStreamId, setActiveStreamId] = useState(null);
  const [savingTargeting, setSavingTargeting] = useState(false);
  const [savingStream, setSavingStream] = useState(false);
  const [poolCount, setPoolCount]     = useState(null);
  const [loadingPool, setLoadingPool] = useState(false);

  // Visitor outreach
  const [visitor, setVisitor]         = useState({
    is_active: false, min_pageviews: 2, lookback_hours: 24,
    daily_cap: 3, auto_send: false, ai_guidance: '',
  });
  const [savingVisitor, setSavingVisitor] = useState(false);

  // Stream monitor
  const [monitor, setMonitor]         = useState({
    is_active: true, no_open_days: 7, escalate_on_click: true,
    bounce_threshold: 2, auto_apply: false,
  });
  const [savingMonitor, setSavingMonitor] = useState(false);

  // Master toggle
  const [dashEnabled, setDashEnabled] = useState(false);
  const [togglingDash, setTogglingDash] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o, opts, st] = await Promise.all([
        dashFetch('/api/console/dash/summary'),
        dashFetch('/api/console/dash/outbox?status=pending'),
        b2bGetContactOptions().catch(() => ({})),
        b2bGetStreams().catch(() => []),
      ]);

      setSummary(s);
      setInbox(o?.items || []);
      setContactOpts(opts || {});
      setStreams(Array.isArray(st) ? st : (st?.streams || st?.items || []));

      const cfg = s?.config || [];
      setDashEnabled(cfg.some(c => c.is_active));

      const ct = cfg.find(c => c.config_key === 'contact_targeting');
      if (ct) {
        const settings = tryParse(ct.settings);
        setTargeting({
          contacts_per_day: settings.contacts_per_day || 10,
          daily_send_cap: settings.daily_send_cap || 20,
          industries: settings.industries || [],
          sizes: settings.sizes || [],
          titles: settings.titles || [],
          auto_send: !!ct.auto_send,
          exclude_in_list: !!settings.exclude_in_list,
        });
        setActiveStreamId(settings.active_stream_id || null);
      }

      const vo = cfg.find(c => c.config_key === 'visitor_outreach');
      if (vo) {
        const settings = tryParse(vo.settings);
        setVisitor({
          is_active: !!vo.is_active,
          min_pageviews: settings.min_pageviews || 2,
          lookback_hours: settings.lookback_hours || 24,
          daily_cap: settings.daily_cap || 3,
          auto_send: !!vo.auto_send,
          ai_guidance: vo.ai_guidance || '',
        });
      }

      const sm = cfg.find(c => c.config_key === 'stream_monitor');
      if (sm) {
        const settings = tryParse(sm.settings);
        setMonitor({
          is_active: !!sm.is_active,
          no_open_days: settings.no_open_days || 7,
          escalate_on_click: settings.escalate_on_click !== false,
          bounce_threshold: settings.bounce_threshold || 2,
          auto_apply: !!sm.auto_send,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function fetchPoolCount(t) {
    setLoadingPool(true);
    try {
      const params = new URLSearchParams();
      if (t.industries.length)  params.set('industries',      t.industries.join('|'));
      if (t.titles.length)      params.set('titles',          t.titles.join('|'));
      if (t.sizes.length)       params.set('sizes',           t.sizes.join('|'));
      if (t.exclude_in_list)    params.set('exclude_in_list', '1');
      const r = await dashFetch(`/api/console/dash/pool-count?${params}`);
      setPoolCount(typeof r.pool_count === 'number' ? r.pool_count : null);
    } catch { setPoolCount(null); }
    finally { setLoadingPool(false); }
  }

  // Auto-refresh pool count when filters change (debounced)
  useEffect(() => {
    const id = setTimeout(() => {
      fetchPoolCount(targeting);
    }, 700);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targeting.industries.join('|'), targeting.titles.join('|'), targeting.sizes.join('|'), targeting.exclude_in_list]);

  useEffect(() => {
    if (activeTab !== 'results') return;
    dashFetch(`/api/console/dash/template-stats?range=${resultsRange}`)
      .then(r => setResults(r))
      .catch(() => {});
  }, [activeTab, resultsRange]);

  function toggleChip(arr, val) {
    return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  }

  async function saveTargeting() {
    setSavingTargeting(true);
    try {
      await dashFetch('/api/console/dash/config/contact_targeting', {
        method: 'PATCH',
        body: {
          is_active: true,
          auto_send: targeting.auto_send,
          settings: {
            contacts_per_day: +targeting.contacts_per_day,
            daily_send_cap: +targeting.daily_send_cap,
            industries: targeting.industries,
            sizes: targeting.sizes,
            titles: targeting.titles,
            active_stream_id: activeStreamId,
            exclude_in_list: targeting.exclude_in_list,
          },
        },
      });
    } catch (e) { alert(e.message); }
    finally { setSavingTargeting(false); }
  }

  async function saveVisitor() {
    setSavingVisitor(true);
    try {
      await dashFetch('/api/console/dash/config/visitor_outreach', {
        method: 'PATCH',
        body: {
          is_active: visitor.is_active,
          auto_send: visitor.auto_send,
          ai_guidance: visitor.ai_guidance,
          settings: {
            min_pageviews: +visitor.min_pageviews,
            lookback_hours: +visitor.lookback_hours,
            daily_cap: +visitor.daily_cap,
          },
        },
      });
    } catch (e) { alert(e.message); }
    finally { setSavingVisitor(false); }
  }

  async function saveMonitor() {
    setSavingMonitor(true);
    try {
      await dashFetch('/api/console/dash/config/stream_monitor', {
        method: 'PATCH',
        body: {
          is_active: monitor.is_active,
          auto_send: monitor.auto_apply,
          settings: {
            no_open_days: +monitor.no_open_days,
            escalate_on_click: monitor.escalate_on_click,
            bounce_threshold: +monitor.bounce_threshold,
          },
        },
      });
    } catch (e) { alert(e.message); }
    finally { setSavingMonitor(false); }
  }

  async function toggleDash() {
    setTogglingDash(true);
    const newVal = !dashEnabled;
    try {
      const cfg = summary?.config || [];
      if (cfg.length) {
        await Promise.all(cfg.map(c =>
          dashFetch(`/api/console/dash/config/${c.config_key}`, { method: 'PATCH', body: { is_active: newVal } })
        ));
      }
      setDashEnabled(newVal);
    } catch (e) { alert(e.message); }
    finally { setTogglingDash(false); }
  }

  async function approveItem(item) {
    setApprovingId(item.outbox_id);
    try {
      await dashFetch(`/api/console/dash/outbox/${item.outbox_id}/approve`, { method: 'POST', body: {} });
      setInbox(o => o.filter(x => x.outbox_id !== item.outbox_id));
      setPreviewItem(null);
    } catch (e) { alert(e.message); }
    finally { setApprovingId(null); }
  }

  async function rejectItem(item) {
    try {
      await dashFetch(`/api/console/dash/outbox/${item.outbox_id}/reject`, { method: 'POST', body: {} });
      setInbox(o => o.filter(x => x.outbox_id !== item.outbox_id));
      setPreviewItem(null);
    } catch (e) { alert(e.message); }
  }

  const pending = summary?.pending || 0;
  const week    = summary?.week   || {};
  const templates = results?.items || [];

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb' }}>
        <AdminSidebar active="dash" />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading Dash…</div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="dash" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #0a2540 0%, #0f3460 50%, #162d4a 100%)',
          padding: '32px 36px 28px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
              <div style={s.heroIconWrap}>⚡</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Dash</h1>
                  <span style={{ background: 'rgba(243,116,40,0.2)', color: '#F37428', fontSize: 9,
                    fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 10,
                    textTransform: 'uppercase', border: '1px solid rgba(243,116,40,0.3)' }}>Bot 3</span>
                </div>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 520, lineHeight: 1.5 }}>
                  Outbound sales · Contact targeting · Email streams
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
              {dashEnabled ? (
                <span style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', fontSize: 11,
                  fontWeight: 700, padding: '4px 12px', borderRadius: 10,
                  border: '1px solid rgba(52,211,153,0.3)' }}>✓ Active</span>
              ) : (
                <span style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: 11,
                  fontWeight: 700, padding: '4px 12px', borderRadius: 10,
                  border: '1px solid rgba(148,163,184,0.3)' }}>Standby</span>
              )}
              <button onClick={toggleDash} disabled={togglingDash} style={{
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600, borderRadius: 8,
                padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                {togglingDash ? '…' : dashEnabled ? '⏸ Pause Dash' : '▶ Enable Dash'}
              </button>
            </div>
          </div>
        </div>

      <div style={s.page}>

        {/* ── Stats ────────────────────────────────────────────── */}
        <div style={s.statsRow}>
          {[
            { label: 'Sent (7d)',     val: week.sent      || 0,    color: '#0f172a' },
            { label: 'Pending review',val: pending,                color: pending > 0 ? ORANGE : '#0f172a' },
            { label: 'Open rate',     val: week.open_rate  ? week.open_rate + '%'  : '—', color: '#10b981' },
            { label: 'Click rate',    val: week.click_rate ? week.click_rate + '%' : '—', color: '#0f172a' },
            { label: 'Bounce rate',   val: week.bounce_rate ? week.bounce_rate + '%' : '—', color: '#ef4444' },
          ].map(({ label, val, color }) => (
            <div key={label} style={s.statCard}>
              <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* ── Tabs ─────────────────────────────────────────────── */}
        <div style={s.tabBar}>
          {[
            { id: 'outreach', label: '📤 Outreach' },
            { id: 'monitor',  label: '📡 Stream Monitor' },
            { id: 'inbox',    label: `💬 Inbox${inbox.length > 0 ? ` (${inbox.length})` : ''}` },
            { id: 'results',  label: '📊 Results' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ ...s.tab, ...(activeTab === t.id ? s.tabActive : {}) }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════
            OUTREACH TAB
        ══════════════════════════════════════════════════════ */}
        {activeTab === 'outreach' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

              {/* Contact Targeting */}
              <div style={s.card}>
                <CardHead
                  title="Contact Targeting"
                  sub="Who Dash picks from your contact database each day"
                  right={<Toggle on={true} onChange={() => {}} />}
                />
                <div style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={s.fieldLabel}>
                        Contacts / day&nbsp;
                        <HelpIcon tip="How many new contacts Dash enrolls in a stream each day. Start low (5–10) to protect your sender reputation." />
                      </label>
                      <input type="number" min={1} max={500} value={targeting.contacts_per_day}
                        onChange={e => setTargeting(t => ({ ...t, contacts_per_day: e.target.value }))}
                        style={{ ...s.input, width: 80 }} />
                    </div>
                    <div>
                      <label style={s.fieldLabel}>
                        Daily send cap&nbsp;
                        <HelpIcon tip="Maximum total emails sent per day across all streams. Prevents volume spikes that trigger spam filters. Recommended: no more than 50/day when starting out." />
                      </label>
                      <input type="number" min={1} max={1000} value={targeting.daily_send_cap}
                        onChange={e => setTargeting(t => ({ ...t, daily_send_cap: e.target.value }))}
                        style={{ ...s.input, width: 80 }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={s.filterLabel}>
                      Target Industries&nbsp;
                      <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(from your DB)</span>
                    </div>
                    <div style={s.chips}>
                      {(contactOpts.industries || []).length === 0
                        ? <span style={{ fontSize: 11, color: '#94a3b8' }}>No industries in DB yet</span>
                        : (contactOpts.industries || []).map(ind => (
                          <Chip key={ind} label={ind} on={targeting.industries.includes(ind)}
                            onClick={() => setTargeting(t => ({ ...t, industries: toggleChip(t.industries, ind) }))} />
                        ))
                      }
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={s.filterLabel}>Company Size</div>
                    <div style={s.chips}>
                      {SIZE_OPTIONS.map(sz => (
                        <Chip key={sz} label={sz} on={targeting.sizes.includes(sz)}
                          onClick={() => setTargeting(t => ({ ...t, sizes: toggleChip(t.sizes, sz) }))} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={s.filterLabel}>
                      Target Titles&nbsp;
                      <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(from your DB)</span>
                    </div>
                    <div style={s.chips}>
                      {(contactOpts.standard_titles || []).length === 0
                        ? <span style={{ fontSize: 11, color: '#94a3b8' }}>No standard titles in DB yet</span>
                        : (contactOpts.standard_titles || []).map(t => (
                          <Chip key={t} label={t} on={targeting.titles.includes(t)}
                            onClick={() => setTargeting(tt => ({ ...tt, titles: toggleChip(tt.titles, t) }))} />
                        ))
                      }
                    </div>
                  </div>

                  {/* Exclude already-listed contacts */}
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" id="excl-in-list"
                      checked={targeting.exclude_in_list}
                      onChange={e => setTargeting(t => ({ ...t, exclude_in_list: e.target.checked }))}
                      style={{ width: 15, height: 15, accentColor: ORANGE, cursor: 'pointer', flexShrink: 0 }} />
                    <label htmlFor="excl-in-list" style={{ fontSize: 12, color: '#374151', cursor: 'pointer', lineHeight: 1.4 }}>
                      Only contact people not already in any list
                    </label>
                  </div>

                  {/* Pool count */}
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                    border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: '0.05em', marginBottom: 2 }}>Available Pool</div>
                      {loadingPool
                        ? <div style={{ fontSize: 20, fontWeight: 700, color: '#94a3b8' }}>…</div>
                        : poolCount === null
                          ? <div style={{ fontSize: 12, color: '#94a3b8' }}>—</div>
                          : <div style={{ fontSize: 20, fontWeight: 700,
                              color: poolCount < 20 ? '#ef4444' : poolCount < 100 ? ORANGE : '#10b981' }}>
                              {poolCount.toLocaleString()}
                            </div>
                      }
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>contacts match your filters</div>
                    </div>
                    <button onClick={() => fetchPoolCount(targeting)} disabled={loadingPool}
                      style={{ fontSize: 11, fontWeight: 600, color: '#64748b', background: 'none',
                        border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px',
                        cursor: loadingPool ? 'not-allowed' : 'pointer' }}>
                      {loadingPool ? '…' : '↻ Refresh'}
                    </button>
                  </div>

                </div>
                <CardFooter
                  label="Auto-send without review"
                  sub={targeting.auto_send ? 'On — emails send automatically' : 'Off — all emails go to review queue first'}
                  toggleVal={targeting.auto_send}
                  onToggle={v => setTargeting(t => ({ ...t, auto_send: v }))}
                  onSave={saveTargeting}
                  saving={savingTargeting}
                />
              </div>

              {/* Active Stream */}
              <div style={s.card}>
                <CardHead
                  title="Active Stream"
                  sub="Pick the stream Dash uses this week for all targeted contacts"
                />
                <div style={{ padding: '14px 18px' }}>
                  <p style={{ fontSize: 11, color: '#64748b', marginBottom: 14, lineHeight: 1.6 }}>
                    All contacts picked by the targeting rules will be enrolled in this stream.
                    Swap streams weekly to vary your messaging.
                  </p>
                  {streams.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
                      No streams configured yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {streams.map(st => {
                        const isActive = st.stream_id === activeStreamId;
                        return (
                          <div key={st.stream_id} onClick={() => setActiveStreamId(isActive ? null : st.stream_id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                              borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                              border: `2px solid ${isActive ? ORANGE : '#e2e8f0'}`,
                              background: isActive ? 'rgba(243,116,40,0.05)' : '#fafcff',
                            }}>
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${isActive ? ORANGE : '#cbd5e1'}`,
                              background: isActive ? ORANGE : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isActive && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? NAVY : '#374151' }}>{st.stream_name}</div>
                              {st.from_email && (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                                  From: {st.from_name || st.from_email} &lt;{st.from_email}&gt;
                                </div>
                              )}
                            </div>
                            {isActive && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: ORANGE, letterSpacing: '0.08em',
                                textTransform: 'uppercase', background: 'rgba(243,116,40,0.1)',
                                padding: '2px 8px', borderRadius: 6 }}>Active</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={async () => {
                      setSavingStream(true);
                      try {
                        await dashFetch('/api/console/dash/config/contact_targeting', {
                          method: 'PATCH',
                          body: {
                            settings: {
                              contacts_per_day:  +targeting.contacts_per_day,
                              daily_send_cap:    +targeting.daily_send_cap,
                              industries:        targeting.industries,
                              sizes:             targeting.sizes,
                              titles:            targeting.titles,
                              exclude_in_list:   targeting.exclude_in_list,
                              active_stream_id:  activeStreamId,
                            },
                          },
                        });
                      } catch (e) { alert(e.message); }
                      finally { setSavingStream(false); }
                    }} disabled={savingStream} style={s.btnSave}>
                      {savingStream ? 'Saving…' : 'Save stream'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Visitor Outreach — full width */}
            <div style={s.card}>
              <CardHead
                title="👁 Visitor Outreach"
                sub="Draft a warm email when a known contact visits your website"
                right={<Toggle on={visitor.is_active} onChange={v => setVisitor(c => ({ ...c, is_active: v }))} />}
              />
              <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <label style={s.fieldLabel}>Min page views</label>
                    <input type="number" min={1} value={visitor.min_pageviews}
                      onChange={e => setVisitor(c => ({ ...c, min_pageviews: e.target.value }))}
                      style={{ ...s.input, width: 80 }} />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>Lookback window</label>
                    <select value={visitor.lookback_hours}
                      onChange={e => setVisitor(c => ({ ...c, lookback_hours: +e.target.value }))}
                      style={s.select}>
                      <option value={24}>Last 24 hours</option>
                      <option value={48}>Last 48 hours</option>
                      <option value={168}>Last 7 days</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.fieldLabel}>Daily cap</label>
                    <input type="number" min={1} value={visitor.daily_cap}
                      onChange={e => setVisitor(c => ({ ...c, daily_cap: e.target.value }))}
                      style={{ ...s.input, width: 70 }} />
                  </div>
                </div>
                <div>
                  <label style={s.fieldLabel}>AI Guidance</label>
                  <textarea value={visitor.ai_guidance}
                    onChange={e => setVisitor(c => ({ ...c, ai_guidance: e.target.value }))}
                    placeholder="Tell the AI how to write visitor outreach emails. Be specific about tone and what to reference."
                    style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 7, padding: '8px 10px',
                      fontSize: 11, fontFamily: 'inherit', resize: 'vertical', minHeight: 60,
                      color: '#374151', boxSizing: 'border-box' }} />
                </div>
              </div>
              <CardFooter
                label="Auto-send visitor outreach"
                sub={visitor.auto_send ? 'On — sends automatically' : 'Off — goes to review queue first'}
                toggleVal={visitor.auto_send}
                onToggle={v => setVisitor(c => ({ ...c, auto_send: v }))}
                onSave={saveVisitor}
                saving={savingVisitor}
              />
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            STREAM MONITOR TAB
        ══════════════════════════════════════════════════════ */}
        {activeTab === 'monitor' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            <div style={s.card}>
              <CardHead
                title="What is Stream Monitor?"
                right={<Toggle on={monitor.is_active} onChange={v => setMonitor(m => ({ ...m, is_active: v }))} />}
              />
              <div style={{ padding: '14px 18px' }}>
                <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.75, marginBottom: 12 }}>
                  Once a contact is enrolled in a stream, Dash watches what they do. If they haven't opened a single email after a set number of days, Dash pauses them automatically — no point burning more sends on someone who isn't engaging.
                </p>
                <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.75, marginBottom: 12 }}>
                  If someone <strong>clicks a link</strong>, Dash flags them as a hot lead and moves them out of the regular sequence so you can follow up personally.
                </p>
                <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.75 }}>
                  Dash also watches for <strong>bounce rate spikes</strong> — if your bounce rate exceeds the threshold, it pauses sending and alerts you before your domain reputation takes damage.
                </p>
              </div>
              <div style={{ padding: '14px 18px', borderTop: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <label style={s.fieldLabel}>Pause after no open</label>
                    <select value={monitor.no_open_days}
                      onChange={e => setMonitor(m => ({ ...m, no_open_days: +e.target.value }))}
                      style={s.select}>
                      {[3, 7, 14, 30].map(d => <option key={d} value={d}>{d} days</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.fieldLabel}>Escalate on click</label>
                    <select value={monitor.escalate_on_click ? 'yes' : 'no'}
                      onChange={e => setMonitor(m => ({ ...m, escalate_on_click: e.target.value === 'yes' }))}
                      style={s.select}>
                      <option value="yes">Yes — flag as hot lead</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.fieldLabel}>
                      Bounce alert threshold&nbsp;
                      <HelpIcon tip="If your bounce rate exceeds this percentage, Dash pauses all sending and notifies you. Keeping bounces below 2% protects your email sender reputation." />
                    </label>
                    <select value={monitor.bounce_threshold}
                      onChange={e => setMonitor(m => ({ ...m, bounce_threshold: +e.target.value }))}
                      style={s.select}>
                      {[1, 2, 3, 5].map(v => <option key={v} value={v}>{v}%</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <CardFooter
                label="Auto-apply pauses"
                sub={monitor.auto_apply ? 'On — pauses apply automatically' : 'Off — changes need your approval'}
                toggleVal={monitor.auto_apply}
                onToggle={v => setMonitor(m => ({ ...m, auto_apply: v }))}
                onSave={saveMonitor}
                saving={savingMonitor}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Alerts */}
              <div style={s.card}>
                <CardHead title="Alerts" sub="Things that need your attention" />
                <div style={{ padding: '4px 18px 12px' }}>
                  {(summary?.alerts || []).length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>
                      No alerts — everything looks good
                    </div>
                  ) : (
                    (summary?.alerts || []).map((a, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0',
                        borderBottom: i < (summary.alerts.length - 1) ? '1px solid #f1f5f9' : 'none' }}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>
                          {a.type === 'bounce' ? '⚠️' : a.type === 'hot_lead' ? '🔥' : '💡'}
                        </span>
                        <div style={{ flex: 1, fontSize: 12, color: '#374151', lineHeight: 1.5 }}
                          dangerouslySetInnerHTML={{ __html: a.message }} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>{timeAgo(a.created_at)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Active Enrollments */}
              <div style={s.card}>
                <CardHead title="Active Enrollments" sub="Contacts currently in a stream" />
                <div style={{ overflowX: 'auto' }}>
                  {(summary?.enrollments || []).length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 12, padding: '24px 18px', textAlign: 'center' }}>
                      No active enrollments yet
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['Contact', 'Stream', 'Step', 'Status'].map(h => (
                            <th key={h} style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
                              letterSpacing: '0.06em', padding: '8px 12px', textAlign: 'left',
                              borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(summary?.enrollments || []).map((e, i) => (
                          <tr key={i}>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc' }}>
                              <div style={{ fontWeight: 600, color: NAVY, fontSize: 12 }}>{e.contact_name}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{e.company} · {e.title}</div>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 11, color: '#374151', borderBottom: '1px solid #f8fafc' }}>{e.stream_name}</td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc' }}>
                              <span style={{ background: '#f1f5f9', borderRadius: 10, padding: '2px 8px',
                                fontSize: 10, fontWeight: 600, color: '#64748b' }}>Step {e.step}</span>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 11, borderBottom: '1px solid #f8fafc' }}>
                              {e.status === 'hot' ? <span style={{ color: '#c2410c', fontWeight: 600 }}>🔥 Hot</span>
                                : e.status === 'paused' ? <span style={{ color: '#94a3b8' }}>Paused</span>
                                : <span style={{ color: '#0176CE' }}>Active</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            INBOX TAB
        ══════════════════════════════════════════════════════ */}
        {activeTab === 'inbox' && (
          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, lineHeight: 1.6 }}>
              Emails drafted by Dash waiting for your approval before sending.
            </div>
            {inbox.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '60px 0', textAlign: 'center' }}>
                No pending emails — inbox is clear
              </div>
            ) : inbox.map(item => (
              <div key={item.outbox_id} style={s.inboxRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <ContextBadge type={item.context_type} />
                  <span style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>
                    {item.to_name || item.to_email}
                  </span>
                  {item.title && <span style={{ fontSize: 11, color: '#64748b' }}>· {item.title}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>{timeAgo(item.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>{item.subject}</div>
                {item.snippet && (
                  <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 10 }}>"{item.snippet}"</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setPreviewItem(item)} style={s.btnSm}>Preview</button>
                  <button onClick={() => approveItem(item)} disabled={approvingId === item.outbox_id}
                    style={{ ...s.btnSm, background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', fontWeight: 600 }}>
                    {approvingId === item.outbox_id ? '…' : '✓ Send'}
                  </button>
                  <button onClick={() => rejectItem(item)}
                    style={{ ...s.btnSm, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            RESULTS TAB
        ══════════════════════════════════════════════════════ */}
        {activeTab === 'results' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              {[
                { id: '7d',  label: '7 days'  },
                { id: '30d', label: '30 days' },
                { id: '90d', label: '90 days' },
              ].map(r => (
                <button key={r.id} onClick={() => setResultsRange(r.id)} style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${resultsRange === r.id ? NAVY : '#e2e8f0'}`,
                  background: resultsRange === r.id ? NAVY : '#fff',
                  color: resultsRange === r.id ? '#fff' : '#64748b',
                }}>{r.label}</button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Emails sent',  val: results?.sent       ?? '—', color: NAVY },
                { label: 'Open rate',    val: results?.open_rate   ? results.open_rate + '%'  : '—', color: '#10b981' },
                { label: 'Click rate',   val: results?.click_rate  ? results.click_rate + '%' : '—', color: ORANGE },
                { label: 'Replies',      val: results?.replies     ?? '—', color: '#0176CE' },
                { label: 'Bounce rate',  val: results?.bounce_rate ? results.bounce_rate + '%' : '—', color: '#ef4444' },
              ].map(({ label, val, color }) => (
                <div key={label} style={s.kpiCard}>
                  <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 5 }}>{label}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Stream / Template', 'Sent', 'Opens', 'Clicks', 'Replies', 'Bounces', ''].map(h => (
                      <th key={h} style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                        letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left',
                        borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        No results yet for this period
                      </td>
                    </tr>
                  ) : templates.map((t, i) => (
                    <tr key={t.template_id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 700, color: NAVY, fontSize: 12 }}>{t.name}</div>
                        {t.subject && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{t.subject}</div>}
                      </td>
                      <td style={s.td}>{t.sends || 0}</td>
                      <td style={s.td}><RateBar rate={t.open_rate} color="#6c63ff" /></td>
                      <td style={s.td}><RateBar rate={t.click_rate} color={ORANGE} /></td>
                      <td style={s.td}>{t.replies || 0}</td>
                      <td style={s.td}>
                        <span style={{ color: (parseFloat(t.bounce_rate) || 0) > 2 ? '#ef4444' : '#374151' }}>
                          {t.bounce_rate ? t.bounce_rate + '%' : '—'}
                        </span>
                      </td>
                      <td style={s.td}>
                        {(parseFloat(t.open_rate) || 0) > 30 && (
                          <span style={{ background: '#f0fdf4', color: '#15803d', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8 }}>⭐ Top</span>
                        )}
                        {(parseFloat(t.bounce_rate) || 0) > 2 && (
                          <span style={{ background: '#fff7ed', color: '#c2410c', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8 }}>⚠ High bounces</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Email Preview Modal ─────────────────────────────── */}
        {previewItem && (
          <div style={s.modalOverlay} onClick={() => setPreviewItem(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{previewItem.to_name || previewItem.to_email}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{previewItem.subject}</div>
                </div>
                <button onClick={() => setPreviewItem(null)} style={s.closeBtn}>✕</button>
              </div>
              <iframe srcDoc={previewItem.html_body} style={{ flex: 1, border: 'none', minHeight: 400 }} title="Preview" />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
                <button onClick={() => rejectItem(previewItem)}
                  style={{ ...s.btnSm, background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}>Reject</button>
                <button onClick={() => approveItem(previewItem)} disabled={approvingId === previewItem.outbox_id}
                  style={{ ...s.btnSave }}>
                  {approvingId === previewItem.outbox_id ? 'Sending…' : 'Approve & Send'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      </main>
    </div>
  );
}

function ContextBadge({ type }) {
  const MAP = {
    apollo:  ['🚀', '#ede9fe', '#7c3aed'],
    visitor: ['👁',  '#dbeafe', '#1d4ed8'],
    reply:   ['💬', '#d1fae5', '#065f46'],
    stream:  ['📡', '#fff7ed', '#c2410c'],
  };
  const [icon, bg, color] = MAP[type] || ['⚙', '#f1f5f9', '#64748b'];
  return (
    <span style={{ background: bg, color, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>
      {icon} {type}
    </span>
  );
}

const s = {
  page:        { padding: '24px 36px 100px', maxWidth: 1200 },
  heroIconWrap:{ width: 52, height: 52, background: 'rgba(243,116,40,0.15)', border: '1px solid rgba(243,116,40,0.3)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 },
  statsRow:    { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 22 },
  statCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' },
  tabBar:      { display: 'flex', gap: 6, marginBottom: 22, background: '#e2e8f0', borderRadius: 10, padding: 4, width: 'fit-content' },
  tab:         { padding: '7px 18px', border: 'none', borderRadius: 8, background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' },
  tabActive:   { background: '#fff', color: '#0a2540', fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  card:        { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' },
  kpiCard:     { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' },
  inboxRow:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 10 },
  ruleRow:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 },
  streamBadge: { background: 'rgba(1,118,206,0.08)', color: '#0176CE', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  addRuleBtn:  { background: 'none', border: '1px dashed #e2e8f0', color: '#94a3b8', fontSize: 12, borderRadius: 8, padding: 8, width: '100%', cursor: 'pointer', marginTop: 4 },
  fieldLabel:  { display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: 5 },
  input:       { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', color: '#1e293b' },
  select:      { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', color: '#1e293b' },
  btnSave:     { background: ORANGE, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSm:       { padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer' },
  td:          { padding: '12px 14px', borderTop: '1px solid #f1f5f9', verticalAlign: 'middle' },
  modalOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  modal:       { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  closeBtn:    { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#94a3b8', padding: 4 },
};
